import {
  searchShippingOrders,
  refetchShippingOrders,
  getShippingDetail,
  scanItem,
  shipProduct,
} from "src/apis/pda1131Api";
import { PDA1131_R01Dto, PDA1131_R01SearchParams } from "./PDA1131_R01Dto";
import PDA1131_R01Store from "./PDA1131_R01Store";
import { useToast } from "src/hooks/useToast";

/** PDA1131_R01 비즈니스 로직 훅 — API 호출·검증·store 갱신 */
export const usePDA1131_R01Content = () => {
  // 목록/상세/스캔/검수 관련 store 상태·setter
  const {
    searchParams,
    setShippingOrderList,
    setSelectedShippingOrder,
    setShippingDetail,
    setShowList,
    setLoading,
    resetDetail,
    itemCdInput,
    setItemCdInput,
    scannedItem,
    setScannedItem,
    shippingDetail,
    inspectedItems,
    addOrMergeInspectedItem,
    clearInspectedItems,
  } = PDA1131_R01Store();

  /** 토스트 알림 */
  const { showToast } = useToast();

  /** store 검색조건 → API 요청 파라미터 변환 (날짜 YYYY-MM-DD) */
  const buildSearchParams = (): PDA1131_R01SearchParams => {
    const params: PDA1131_R01SearchParams = {};
    if (searchParams.orderNo) params.orderNo = searchParams.orderNo;
    if (searchParams.startDate) params.startDate = searchParams.startDate.split("T")[0];
    if (searchParams.endDate) params.endDate = searchParams.endDate.split("T")[0];
    return params;
  };

  /** 출하지시 목록 API 공통 호출 (search / refetch) */
  const fetchOrders = async (
    apiFn: (params: PDA1131_R01SearchParams) => Promise<unknown>
  ) => {
    try {
      setLoading(true);
      const response = await apiFn(buildSearchParams()) as {
        data?: { data?: PDA1131_R01Dto[] };
      };
      const data = response?.data?.data || [];
      setShippingOrderList(data);
      if (data.length === 0) showToast("warning", "조회 결과가 없습니다.");
    } catch (error) {
      console.error("출하지시 조회 실패:", error);
      setShippingOrderList([]);
      showToast("error", "출하지시 조회에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  /** 조회 버튼 — /search API */
  const handleSearch = () => fetchOrders(searchShippingOrders);
  /** 새로고침 — /refetch API */
  const handleRefetch = () => fetchOrders(refetchShippingOrders);

  /** 출하지시 선택 → 상세 화면 (LOT 사전로딩 없음) */
  const handleSelectOrder = async (order: PDA1131_R01Dto) => {
    if (!order.orderNo) {
      console.error("출하지시 번호가 없습니다.");
      return;
    }

    try {
      setLoading(true);
      const response = await getShippingDetail(order.orderNo);
      const detail = response?.data?.data;

      if (!detail) throw new Error("출하지시 상세 정보를 찾을 수 없습니다.");
      if (!detail.itemList || detail.itemList.length === 0) {
        showToast("warning", "출고할 제품이 없습니다.");
        return;
      }

      setSelectedShippingOrder(order);
      setShippingDetail(detail);
      setShowList(false);
    } catch (error: unknown) {
      console.error("출하지시 상세 조회 실패:", error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        || "출하지시 상세 조회에 실패했습니다.";
      showToast("error", message);
    } finally {
      setLoading(false);
    }
  };

  /** 검수 대기 있으면 confirm 후 상세 초기화·목록 전환 */
  const handleBackToList = () => {
    const { inspectedItems: pending, clearInspectedItems: clear } = PDA1131_R01Store.getState();

    if (pending.length > 0) {
      const confirm = window.confirm(
        `검수 대기중인 ${pending.length}건의 항목이 있습니다.\n저장하지 않고 나가시겠습니까?`
      );
      if (!confirm) return;
      clear();
    }

    resetDetail();
    setShowList(true);
  };

  /**
   * itemCd 스캔
   * 1) 출하지시에 있는 품목인지 검증
   * 2) 가용재고 조회 (백엔드)
   * 3) 지시잔량 / 기존 누적분 계산
   * 4) canQty = min(가용, 지시잔량) - 누적
   * 5) scannedItem 세팅 (출고수량 기본값은 DetailInfo에서 canQty로)
   */
  const handleScanItem = async () => {
    const code = itemCdInput.trim();
    if (!code) {
      showToast("error", "품목코드를 입력하세요.");
      return;
    }
    if (!shippingDetail) return;

    try {
      setLoading(true);
      const response = await scanItem(
        code,
        shippingDetail.warehouseCd,
        shippingDetail.logicCenter
      );
      const scan = response?.data?.data;
      if (!scan) throw new Error("품목 정보를 찾을 수 없습니다.");

      const matched = shippingDetail.itemList.find((i) => i.itemCd === scan.itemCd);
      if (!matched) {
        showToast("error", "해당 출고 목록에 없는 품목입니다.");
        setScannedItem(null);
        return;
      }

      const remainOrderQty = matched.orderQty - matched.inspQty;
      const already = inspectedItems
        .filter((i) => i.itemCd === scan.itemCd)
        .reduce((sum, i) => sum + i.shipQty, 0);

      const canQty = Math.min(scan.availableQty, remainOrderQty) - already;

      if (canQty <= 0) {
        showToast(
          "error",
          `더 검수할 수 없습니다. (지시잔량: ${remainOrderQty}, 가용: ${scan.availableQty}, 누적: ${already})`
        );
        setScannedItem(null);
        return;
      }

      setScannedItem({ ...scan, remainOrderQty, canQty });

      if (already > 0) {
        showToast("success", `스캔 완료 (가능수량: ${canQty}, 누적: ${already})`);
      } else {
        showToast("success", "품목 스캔 완료");
      }
    } catch (error: unknown) {
      console.error("스캔 실패:", error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        || "품목 스캔에 실패했습니다.";
      showToast("error", message);
      setScannedItem(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 검수 (누적) — 가용 스냅샷 함께 저장
   * 1) 수량 0 이하 2) canQty 초과 체크
   */
  const handleInspect = (shipQty: number) => {
    if (!shippingDetail || !scannedItem) {
      showToast("error", "필요한 정보가 없습니다.");
      return;
    }
    if (shipQty <= 0) {
      showToast("error", "출고수량은 0보다 커야 합니다.");
      return;
    }
    if (shipQty > scannedItem.canQty) {
      showToast("error", `가능 수량을 초과합니다. (가능: ${scannedItem.canQty})`);
      return;
    }

    addOrMergeInspectedItem({
      itemCd: scannedItem.itemCd,
      itemNm: scannedItem.itemNm,
      unit: scannedItem.itemUnit,
      shipQty,
      currentAvailableQty: scannedItem.availableQty,
    });
    showToast("success", `검수 완료 (${inspectedItems.length + 1}건)`);

    setItemCdInput("");
    setScannedItem(null);
  };

  /** 저장 (품목 단위 목록 일괄 전송, 주문 단위 1회) */
  const handleSaveAll = async () => {
    if (!shippingDetail) {
      showToast("error", "출하지시 정보가 없습니다.");
      return;
    }
    if (inspectedItems.length === 0) {
      showToast("error", "검수된 항목이 없습니다.");
      return;
    }

    try {
      setLoading(true);
      await shipProduct({
        orderNo: shippingDetail.orderNo,
        warehouseCd: shippingDetail.warehouseCd,
        logicCenter: shippingDetail.logicCenter || "",
        remark: "",
        itemList: inspectedItems.map((i) => ({
          itemCd: i.itemCd,
          shipQty: i.shipQty,
          currentAvailableQty: i.currentAvailableQty,
        })),
      });

      const totalQty = inspectedItems.reduce((sum, i) => sum + i.shipQty, 0);
      showToast(
        "success",
        `제품 출고 완료 (품목 ${inspectedItems.length}건, 총 ${totalQty.toLocaleString()}개)`
      );

      clearInspectedItems();

      // 검수량 반영 위해 상세 재조회
      const response = await getShippingDetail(shippingDetail.orderNo);
      if (response?.data?.data) setShippingDetail(response.data.data);
    } catch (error: unknown) {
      console.error("제품 출고 실패:", error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        || "제품 출고에 실패했습니다.";
      showToast("error", message);
    } finally {
      setLoading(false);
    }
  };

  /** 취소 (검수 대기 초기화) */
  const handleCancel = () => {
    if (inspectedItems.length > 0) {
      const confirm = window.confirm(
        `검수 대기중인 ${inspectedItems.length}건의 항목이 있습니다.\n모두 취소하시겠습니까?`
      );
      if (!confirm) return;
      clearInspectedItems();
      showToast("info", "검수 대기 목록이 초기화되었습니다.");
    }

    setItemCdInput("");
    setScannedItem(null);
  };

  return {
    handleSearch,       // 목록 검색
    handleRefetch,      // 목록 새로고침
    handleSelectOrder,  // 출하지시 선택 → 상세
    handleBackToList,   // 상세 → 목록
    handleScanItem,     // itemCd 스캔
    handleInspect,      // 검수 누적
    handleSaveAll,      // 일괄 출고 저장
    handleCancel,       // 검수 대기 취소
  };
};
